import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Dimmer, Loader, Button, Icon } from "semantic-ui-react";
import { useAuth } from "../../../hooks";
import { Sbti } from "../../../api";
import { DOCS } from "../../../components/Admin/SbtiForms/Paso6Tramite";
import "./SbtiReport.scss";

const sbtiController = new Sbti();

// ─────────────────────────────────────────────────────────────────────────
// SBTI-028/030 · Informe SBTi — página imprimible standalone
//
// Portado de generarInforme() del prototipo (línea 1267): en el HTML
// original se abre una ventana nueva con el documento completo y un
// botón "Imprimir / Guardar PDF" (@media print + @page{margin:16mm}).
// Acá el equivalente en una SPA de React es una ruta propia SIN el
// AdminLayout (ver AdminRouter.js) — mismo resultado (una página A4
// lista para Ctrl+P), sin pelear con el CSS del sidebar/header admin.
//
// GAP declarado: el workbook (hoja "3. Backlog", SBTI-028) dice
// "generador con el contenido de la hoja 08 de la Especificación" —
// archivo que no tenemos — y habla de "12 secciones". El prototipo v1
// que sí tenemos (generarInforme) trae portada + 10 secciones numeradas;
// eso es exactamente lo que se porta acá. Si la Especificación real
// define 12, hay que revisar este archivo contra ella.
//
// SBTI-029 (motor de "próximos pasos", ~18 condiciones, sección 11 del
// prototipo) NO se portó: esa función no existe en Sustemia_SBTi_v1 —
// es, otra vez, contenido que solo está en v1.3.1. Portar algo que no
// está en la fuente sería inventarlo, así que queda pendiente, igual
// que SBTI-012.
// ─────────────────────────────────────────────────────────────────────────

const fmt = (v, d = 1) => (isFinite(v) ? Number(v).toLocaleString("es-AR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—");
const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return isFinite(n) ? n : 0; };
const fFecha = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("es-AR") : "—");

const ESTADO_LABEL = { preparacion: "Preparación", comprometida: "Comprometida", presentada: "Presentada", revision: "En revisión", aprobada: "Aprobada", anunciada: "Anunciada" };
const CRIT_ESTADO_LABEL = { ok: "Cumple", no: "No cumple", pe: "Pendiente", na: "N/A", "": "Sin evaluar" };
const CHANNEL_LABEL = { annual_report: "Informe anual", sustainability_report: "Memoria de sostenibilidad", website: "Sitio web", cdp_questionnaire: "Cuestionario CDP", other: "Otro" };
const METHOD_LABEL_12 = { abs: "Contracción absoluta", fisica: "Intensidad física", re: "Electricidad renovable" };
const METHOD_LABEL_3 = { abs: "Contracción absoluta", fisica: "Intensidad física", geva: "Intensidad económica (GEVA)", prov: "Compromiso de proveedores" };

// Portado de tablaTrayectoria() del prototipo (línea 1252) — misma
// interpolación lineal de presentación que ya usa TrajectoryChart/Paso7,
// no una regla nueva.
function trajectoryRows(baseYear, targetYear, baseEmissions, targetEmissions, tracking, scope) {
  if (!(baseYear && targetYear && baseEmissions > 0)) return null;
  const rows = [];
  for (let y = baseYear; y <= targetYear; y++) {
    const f = Math.min(1, (y - baseYear) / (targetYear - baseYear || 1));
    const expected = baseEmissions - (baseEmissions - targetEmissions) * f;
    const row = tracking.find((r) => Number(r.year) === y);
    const real = row ? (scope === "scope12" ? num(row.scope1) + num(row.scope2) : num(row.scope3Covered)) : 0;
    const deviation = real > 0 && expected > 0 ? ((real - expected) / expected) * 100 : null;
    rows.push({ year: y, expected, real, deviation });
  }
  return rows;
}

export function SbtiReport() {
  const { company } = useParams();
  const { accessToken } = useAuth();
  const [record, setRecord] = useState(null);
  const [computed, setComputed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await sbtiController.getExpediente(accessToken, company);
        const { _computed, ...rest } = response;
        setRecord(rest);
        setComputed(_computed);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [accessToken, company]);

  if (loading || !record || !computed) {
    return (
      <div className="sbti-report sbti-report--loading">
        <Dimmer active inverted><Loader>Generando informe…</Loader></Dimmer>
      </div>
    );
  }

  const meta = record.meta || {};
  const inv = record.inventory || {};
  const route = record.route || {};
  const targets = record.targets || {};
  const v = record.validation || {};
  const params = computed.params || {};
  const calc = computed.calc || {};
  const r12 = computed.targetScope12 || {};
  const r3 = computed.targetScope3 || {};
  const t12 = computed.statementScope12 || { es: "", en: "" };
  const t3 = computed.statementScope3 || { es: "", en: "" };
  const criteriaList = computed.criteria || [];
  const items = criteriaList.filter((x) => !x.group);
  const doneCount = items.filter((x) => ["ok", "na"].includes(record.criteria?.[x.id]?.status)).length;
  const today = new Date().toISOString().slice(0, 10);
  const documentsReady = DOCS.filter(([id]) => v.documents?.[id]).length;
  const disclosures = record.disclosures || [];
  const tracking = record.tracking || [];

  const dueDates = computed.dueDates || [];

  const rows12 = trajectoryRows(Number(inv.baseYear), Number(targets.scope12?.targetYear), r12.baseEmissions, r12.targetEmissions, tracking, "scope12");
  const showRows3 = targets.scope3?.targetYear && targets.scope3?.method !== "geva" && targets.scope3?.method !== "prov";
  const rows3 = showRows3 ? trajectoryRows(Number(inv.baseYear), Number(targets.scope3?.targetYear), r3.baseEmissions, r3.targetEmissions, tracking, "scope3") : null;

  return (
    <div className="sbti-report">
      <Button primary className="sbti-report__print noprint" onClick={() => window.print()}>
        <Icon name="print" /> Imprimir / Guardar PDF
      </Button>

      <div className="sbti-report__cover">
        <div className="sbti-report__brand">SUSTE<i>MIA</i></div>
        <h1>Informe SBTi — Objetivos Basados en Ciencia</h1>
        <div className="sbti-report__sub">Preparación, diseño, validación y seguimiento del objetivo · Sector Indumentaria y Calzado</div>
        <div className="sbti-report__meta">
          <span>EMPRESA · {meta.companyName || "—"}</span>
          <span>PAÍS · {meta.country || "—"}</span>
          <span>FECHA · {today}</span>
          <span>MÓDULO · SBTi</span>
          <span>MARCO · {params.criterios_version || "—"} / {params.netzero_version || "—"}</span>
        </div>
      </div>

      <h2>1 · Resumen ejecutivo</h2>
      <table>
        <tbody>
          <tr><td>Estado del proceso</td><td><b>{ESTADO_LABEL[v.status] || "Preparación"}</b> — ruta {route.type === "sme" ? "PYME (streamlined)" : route.type === "corp" ? "corporativa" : "sin definir"}</td></tr>
          <tr><td>Año base</td><td className="num">{inv.baseYear || "—"}</td></tr>
          <tr><td>Emisiones año base (A1+2+3)</td><td className="num">{fmt(calc.totalWithIndirect)} tCO₂e</td></tr>
          <tr><td>A1+A2 ({inv.scope2Approach})</td><td className="num">{fmt((calc.scope1 || 0) + (calc.scope2 || 0))} tCO₂e</td></tr>
          <tr><td>Alcance 3 {inv.excludeCat11 ? "(elegible, sin Cat 11)" : ""} y su peso</td><td className="num">{fmt(calc.scope3Eligible)} tCO₂e · {fmt(calc.scope3Percent)}% {calc.scope3Required ? "⇒ objetivo A3 obligatorio" : "⇒ objetivo A3 no obligatorio"}</td></tr>
          <tr><td>Autochequeo de criterios</td><td>{doneCount}/{items.length} conformes o N/A</td></tr>
        </tbody>
      </table>
      <div className="sbti-report__obj"><b>Objetivo A1+2 (redacción oficial):</b><br />ES — {t12.es}<br /><span className="muted">EN — {t12.en}</span></div>
      {(calc.scope3Required || targets.scope3?.targetYear) && (
        <div className="sbti-report__obj"><b>Objetivo A3 (redacción oficial):</b><br />ES — {t3.es}<br /><span className="muted">EN — {t3.en}</span></div>
      )}
      {targets.netZero?.consider && (
        <div className="sbti-report__obj">
          <b>Net-zero:</b> la empresa fijará además objetivo net-zero (−{params.nz_reduccion ?? 90}% a {targets.netZero.year || params.nz_anio}, cobertura A3 ≥ {params.nz_cobertura_a3 ?? 90}%, neutralización de residuales). {targets.netZero.notes}
        </div>
      )}

      <h2>2 · Inventario GEI del año base</h2>
      <table>
        <thead><tr><th>Alcance</th><th>tCO₂e</th><th>Detalle</th></tr></thead>
        <tbody>
          <tr><td>Alcance 1</td><td className="num">{fmt(calc.scope1)}</td><td>Combustión fija y móvil por fuente + refrigerantes</td></tr>
          <tr><td>Alcance 2 market-based</td><td className="num">{fmt(num(inv.scope2Market))}</td><td rowSpan={2}>Enfoque declarado para el objetivo: <b>{inv.scope2Approach === "market" ? "market-based" : "location-based"}</b></td></tr>
          <tr><td>Alcance 2 location-based</td><td className="num">{fmt(num(inv.scope2Location))}</td></tr>
          <tr><td>Alcance 3 total</td><td className="num">{fmt(calc.scope3Total)}</td><td>15 categorías — detalle en sección 3</td></tr>
          <tr><td><b>Total</b></td><td className="num"><b>{fmt(calc.totalWithIndirect)}</b></td><td>Exclusiones A1+A2 declaradas: {inv.exclusionPercent || "0"}% (máx. {params.exclusion_max ?? 5}%)</td></tr>
        </tbody>
      </table>
      <div className="sbti-report__box">
        <b>Conformidad declarada:</b> GHG Protocol: {inv.ghgProtocolCompliant ? "✔" : "pendiente"} · 7 gases: {inv.sevenGases ? "✔" : "pendiente"} · Bioenergía: {inv.bioenergy ? "✔" : "pendiente"} · Inventario más reciente: {inv.inventoryYear || "—"} {inv.notes ? `· Notas: ${inv.notes}` : ""}
      </div>
      {(record.sites || []).length > 0 && (
        <table>
          <thead><tr><th>Planta</th><th>País</th><th>A1</th><th>A2 ({inv.scope2Approach})</th></tr></thead>
          <tbody>
            {record.sites.map((s, ix) => (
              <tr key={ix}><td>{s.freeName}</td><td>{s.country}</td><td className="num">{fmt(num(s.scope1))}</td><td className="num">{fmt(num(s.scope2))}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>3 · Screening de Alcance 3 y FLAG</h2>
      <table>
        <thead><tr><th>#</th><th>Categoría</th><th>tCO₂e</th><th>% del A3</th><th>En objetivo</th></tr></thead>
        <tbody>
          {(calc.scope3Detail || []).map((cat) => (
            <tr key={cat.id}>
              <td className="num">{cat.n}</td>
              <td>{cat.title}{inv.excludeCat11 && cat.id === "cat11" ? " (excluida — uso indirecto, guía sectorial §6.2)" : ""}</td>
              <td className="num">{cat.value ? fmt(cat.value) : "—"}</td>
              <td className="num">{cat.value && calc.scope3Total ? fmt((cat.value / calc.scope3Total) * 100) + "%" : "—"}</td>
              <td>{record.coverageSelection?.[cat.id] && !(inv.excludeCat11 && cat.id === "cat11") ? "✔" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sbti-report__box">
        Peso del A3: <b>{fmt(calc.scope3Percent)}%</b> (umbral {params.umbral_a3 ?? 40}%) ⇒ objetivo A3 {calc.scope3Required ? <b>obligatorio</b> : "no obligatorio"} · Cobertura seleccionada: <b>{fmt(calc.coveragePercent)}%</b> del A3 elegible (mín. {params.cobertura_a3 ?? 67}%) = {fmt(calc.coveredEmissions)} tCO₂e · FLAG:{" "}
        {inv.flagPercent ? `${fmt(calc.flagPercent)}% (umbral ${params.flag_umbral ?? 20}%) ⇒ objetivo FLAG ${calc.flagRequired ? "requerido" : "no requerido"}` : "sin estimar"} {inv.flagNotes ? `· ${inv.flagNotes}` : ""}
      </div>

      <h2>4 · Diseño de los objetivos near-term</h2>
      <table>
        <thead><tr><th></th><th>A1+2</th><th>A3</th></tr></thead>
        <tbody>
          <tr><td>Método</td><td>{METHOD_LABEL_12[targets.scope12?.method] || "—"}</td><td>{METHOD_LABEL_3[targets.scope3?.method] || "—"}</td></tr>
          <tr><td>Ambición</td><td>1,5 °C (mín. vigente · {params.tasa_15C ?? 4.2}%/año)</td><td>{targets.scope3?.ambition === "1.5" ? `1,5 °C (${params.tasa_15C ?? 4.2}%/año)` : `Well-below 2 °C (mín. vigente · ${params.tasa_WB2C ?? 2.5}%/año)`}</td></tr>
          <tr><td>Año objetivo</td><td className="num">{targets.scope12?.targetYear || "—"} ({r12.years || "—"} años desde base)</td><td className="num">{targets.scope3?.targetYear || "—"} ({r3.years || "—"} años)</td></tr>
          <tr><td>Base del objetivo</td><td className="num">{fmt(r12.baseEmissions)} tCO₂e</td><td className="num">{fmt(r3.baseEmissions)} tCO₂e (cobertura)</td></tr>
          <tr><td>Reducción total</td><td className="num">−{fmt(r12.totalPercent)}%</td><td className="num">−{fmt(r3.totalPercent)}%{targets.scope3?.method === "geva" ? " (intensidad)" : ""}</td></tr>
          <tr><td>Emisiones al año objetivo</td><td className="num">{fmt(r12.targetEmissions)} tCO₂e</td><td className="num">{targets.scope3?.method === "geva" ? "depende del crecimiento del VA" : `${fmt(r3.targetEmissions)} tCO₂e`}</td></tr>
        </tbody>
      </table>
      <div className="sbti-report__box muted">
        Los porcentajes se diseñaron con las tasas lineales mínimas vigentes ({params.tasa_15C ?? 4.2}% / {params.tasa_WB2C ?? 2.5}%). El valor exacto se confirma con la herramienta oficial de target-setting del SBTi antes de presentar.
      </div>

      <h2>5 · Trayectorias año a año</h2>
      <b>A1+2</b>
      {rows12 ? (
        <table>
          <thead><tr><th>Año</th><th>Trayectoria lineal (tCO₂e)</th><th>Real (tCO₂e)</th><th>Desvío</th></tr></thead>
          <tbody>
            {rows12.map((r) => (
              <tr key={r.year}><td className="num">{r.year}</td><td className="num">{fmt(r.expected)}</td><td className="num">{r.real > 0 ? fmt(r.real) : "—"}</td><td className="num">{r.deviation !== null ? `${r.deviation > 0 ? "+" : ""}${fmt(r.deviation)}%` : "—"}</td></tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">Sin datos suficientes para la trayectoria.</p>
      )}
      {showRows3 && (
        <>
          <b>A3 cubierto</b>
          {rows3 ? (
            <table>
              <thead><tr><th>Año</th><th>Trayectoria lineal (tCO₂e)</th><th>Real (tCO₂e)</th><th>Desvío</th></tr></thead>
              <tbody>
                {rows3.map((r) => (
                  <tr key={r.year}><td className="num">{r.year}</td><td className="num">{fmt(r.expected)}</td><td className="num">{r.real > 0 ? fmt(r.real) : "—"}</td><td className="num">{r.deviation !== null ? `${r.deviation > 0 ? "+" : ""}${fmt(r.deviation)}%` : "—"}</td></tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">Sin datos suficientes para la trayectoria.</p>
          )}
        </>
      )}

      <h2>6 · Autochequeo de criterios</h2>
      <table>
        <thead><tr><th>Ref.</th><th>Criterio</th><th>Estado</th><th>Autoevaluación del módulo</th></tr></thead>
        <tbody>
          {criteriaList.map((it, ix) =>
            it.group ? (
              <tr key={`g-${ix}`}><td colSpan={4} className="sbti-report__group-row">{it.group}</td></tr>
            ) : (
              <tr key={it.id}>
                <td className="mono">{it.ref || "—"}</td>
                <td>{it.title}</td>
                <td><span className={`sbti-report__b sbti-report__b--${record.criteria?.[it.id]?.status || "na"}`}>{CRIT_ESTADO_LABEL[record.criteria?.[it.id]?.status || ""]}</span></td>
                <td className="muted">{it.auto?.message || ""}</td>
              </tr>
            )
          )}
        </tbody>
      </table>

      <h2>7 · Proceso de validación — fechas y vencimientos</h2>
      <table>
        <thead><tr><th>Hito</th><th>Fecha</th></tr></thead>
        <tbody>
          <tr><td>Registro en el Validation Portal</td><td className="num">{fFecha(v.registeredOn)}</td></tr>
          <tr><td>Compromiso (opcional, corporativa)</td><td className="num">{fFecha(v.committedOn)}</td></tr>
          <tr><td>Presentación y pago</td><td className="num">{fFecha(v.submittedOn)}</td></tr>
          <tr><td>Inicio de revisión</td><td className="num">{fFecha(v.reviewStartedOn)}</td></tr>
          <tr><td>Aprobación</td><td className="num">{fFecha(v.approvedOn)}</td></tr>
          <tr><td>Anuncio público</td><td className="num">{fFecha(v.announcedOn)}</td></tr>
          {dueDates.map((d) => (
            <tr key={d.label}><td><b>Vencimiento:</b> {d.label}</td><td className="num"><b>{fFecha(d.date)}</b></td></tr>
          ))}
        </tbody>
      </table>
      <div className="sbti-report__box">
        Tarifa: {route.type === "sme" ? params.sme_fee : params.corp_fee} {v.feeNote ? `· Cotización registrada: ${v.feeNote}` : ""} · Documentación lista: {documentsReady}/{DOCS.length} ítems {v.processNotes ? `· Notas del trámite: ${v.processNotes}` : ""}
      </div>

      <h2>8 · Seguimiento anual y divulgación</h2>
      {tracking.length ? (
        <table>
          <thead><tr><th>Año</th><th>A1</th><th>A2 ({inv.scope2Approach})</th><th>A1+2</th><th>A3 cubierto</th><th>Nota</th></tr></thead>
          <tbody>
            {[...tracking].sort((a, b) => num(a.year) - num(b.year)).map((r, ix) => (
              <tr key={ix}><td className="num">{r.year}</td><td className="num">{fmt(num(r.scope1))}</td><td className="num">{fmt(num(r.scope2))}</td><td className="num">{fmt(num(r.scope1) + num(r.scope2))}</td><td className="num">{fmt(num(r.scope3Covered))}</td><td>{r.note}</td></tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">Sin registros anuales cargados todavía.</p>
      )}
      {disclosures.length ? (
        <table>
          <thead><tr><th>Año informado</th><th>Medio</th><th>Fecha</th><th>Referencia</th></tr></thead>
          <tbody>
            {disclosures.map((d, ix) => (
              <tr key={ix}><td className="num">{d.reportedYear}</td><td>{CHANNEL_LABEL[d.channel] || d.channel}</td><td className="num">{fFecha(d.date)}</td><td>{d.reference}</td></tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">Sin divulgaciones públicas registradas todavía (obligación anual tras la aprobación).</p>
      )}

      <h2>9 · Palancas de reducción (marco de la guía sectorial, Figura 9)</h2>
      <div className="sbti-report__box">
        <b>Tiers 1–2 (eficiencia + renovables):</b> incentivar proveedores top, desinvertir de los de bajo desempeño,
        invertir en eficiencia energética, escalar programas existentes (NRDC Clean by Design, IFC), renovables en
        tiers 1 y 2. <b>Tiers 3–4 (materiales):</b> materiales ambientalmente preferibles, eficiencia y renovables
        en procesos, intervenciones a nivel finca/rancho, I+D de materiales de impacto cero. <b>Transversales:</b>{" "}
        eficiencia de material, sustitución (rPoly), cambios de sourcing, inversiones puntuales en la cadena.
        Ejecución y seguimiento en el Plan Anual de Sustemia.
      </div>

      <h2>10 · Marco normativo y fuentes</h2>
      <table>
        <thead><tr><th>Fuente</th><th>Rol</th></tr></thead>
        <tbody>
          <tr><td>{params.criterios_version || "SBTi Corporate Near-Term Criteria"} + Criteria Assessment Indicators v1.7</td><td>Criterios, ambición mínima ({params.tasa_15C ?? 4.2}% / {params.tasa_WB2C ?? 2.5}%), plazos {params.plazo_min ?? 5}–{params.plazo_max ?? 10} años, umbral {params.umbral_a3 ?? 40}% y cobertura {params.cobertura_a3 ?? 67}%</td></tr>
          <tr><td>{params.netzero_version || "SBTi Corporate Net-Zero Standard"}</td><td>Marco de largo plazo</td></tr>
          <tr><td>Guía sectorial Apparel &amp; Footwear (SBTi/WRI, 2019)</td><td>Métodos del sector, Cat 1 y Cat 11, GEVA, marco de reducción y colaboraciones</td></tr>
          <tr><td>FAQs PYME v6.2 + definición PYME (01-ene-2024) · Guía FLAG (mar-2026) · Guía de revisión quinquenal (dic-2025) · SBTi Services</td><td>Ruta de validación, FLAG y ciclo de vida del objetivo</td></tr>
          <tr><td>GHG Protocol (Corporate, Scope 2, Scope 3, Technical Guidance)</td><td>Base contable del inventario</td></tr>
        </tbody>
      </table>
      <p className="muted" style={{ marginTop: 6 }}>
        Fuentes verificadas el {params.verificado || "—"}. Convenciones propias del módulo (banda del semáforo,
        tasas de diseño, base de cobertura) declaradas en la vista «Fuentes y trazabilidad». Este informe es de
        preparación y gestión: no reemplaza la evaluación oficial de SBTi Services.
      </p>

      <div className="sbti-report__foot">
        <span><span className="sbti-report__pg"><span>SUSTEMIA</span></span> · Plataforma de gestión de sostenibilidad · www.sustemia.com</span>
        <span className="mono">Informe_SBTi_{today} · Datos con respaldo de evidencias</span>
      </div>
    </div>
  );
}
